// Calculo de RFC (con homoclave) y CURP a partir de nombre, apellidos, fecha de
// nacimiento, sexo y entidad de nacimiento. Algoritmos oficiales SAT/RENAPO.
// El resultado es una sugerencia editable por el usuario, no un valor definitivo
// (la homonimia/diferenciador de RENAPO depende de su padron, no es calculable).

const VOCALES = ['A', 'E', 'I', 'O', 'U'];

const PALABRAS_OBSCENAS = [
  'BUEI', 'BATO', 'BOFE', 'BUEY', 'CACA', 'CACO', 'CAGO', 'CAKO', 'CAGA', 'CAKA',
  'COGI', 'COJA', 'COJI', 'COJO', 'COLA', 'CULO', 'COGE', 'COJE', 'FALO', 'FOCA',
  'FETO', 'GATA', 'GETA', 'GUEI', 'GUEY', 'JETA', 'JOTO', 'KAKA', 'KAGA', 'KACA',
  'KOGE', 'KOGI', 'KOJA', 'KOJE', 'KOJI', 'KACO', 'KOLA', 'KAGO', 'KOJO', 'KULO',
  'LILO', 'LOBA', 'LOCA', 'LOKA', 'LOKO', 'LORA', 'LORO', 'LOCO', 'MALA', 'MAMA',
  'MEAR', 'MEON', 'MIAR', 'MOCO', 'MOKO', 'MULA', 'MULO', 'MAMO', 'MAME', 'MEAS',
  'MION', 'NACA', 'NACO', 'PEDA', 'PIPI', 'PITO', 'POPO', 'PEDO', 'PUTA', 'QULO',
  'RUIN', 'PENE', 'PUTO', 'RATA', 'ROBA', 'ROBE', 'ROBO', 'SAPO', 'SENO', 'SOPE',
  'TETA', 'VACA', 'VAGA', 'VAGO', 'VUEI', 'VUEY', 'WUEI', 'WUEY'
];

const SUFIJOS_NOMBRES = ['MARIA', 'JOSE', 'MA', 'J', 'DE', 'DEL', 'LOS', 'LAS', 'LA'];
const SUFIJOS_APELLIDOS = ['DE', 'LA', 'LAS', 'MC', 'VON', 'DEL', 'LOS', 'Y', 'MAC', 'VAN'];

// Claves de entidad federativa para CURP (RENAPO), por nombre tal como se
// almacena en la tabla `sepomex`.
const ESTADOS_CURP = {
  'Aguascalientes': 'AS', 'Baja California': 'BC', 'Baja California Sur': 'BS',
  'Campeche': 'CC', 'Coahuila': 'CL', 'Colima': 'CM', 'Chiapas': 'CS',
  'Chihuahua': 'CH', 'Ciudad de México': 'DF', 'Durango': 'DG',
  'Guanajuato': 'GT', 'Guerrero': 'GR', 'Hidalgo': 'HG', 'Jalisco': 'JC',
  'México': 'MC', 'Michoacán': 'MN', 'Morelos': 'MS', 'Nayarit': 'NT',
  'Nuevo León': 'NL', 'Oaxaca': 'OC', 'Puebla': 'PL', 'Querétaro': 'QT',
  'Quintana Roo': 'QR', 'San Luis Potosí': 'SP', 'Sinaloa': 'SL',
  'Sonora': 'SR', 'Tabasco': 'TC', 'Tamaulipas': 'TS', 'Tlaxcala': 'TL',
  'Veracruz': 'VZ', 'Yucatán': 'YN', 'Zacatecas': 'ZS'
};

// Mayusculas, sin acentos, solo letras y espacios. `preservarEnye` mantiene la
// Ñ (necesaria para CURP); si no, la convierte a N (como exige el RFC).
function normalizar(str, preservarEnye = false) {
  let s = (str || '').toUpperCase();
  const ENYE = 'Ñ';
  const MARCA = '';
  const DIACRITICOS = /[̀-ͯ]/g;
  if (preservarEnye) s = s.split(ENYE).join(MARCA);
  s = s.normalize('NFD').replace(DIACRITICOS, '');
  if (preservarEnye) {
    s = s.split(MARCA).join(ENYE).replace(new RegExp('[^A-Z' + ENYE + ' ]', 'g'), '');
  } else {
    s = s.replace(/[^A-Z ]/g, '');
  }
  return s.replace(/\s+/g, ' ').trim();
}

// Compara país/nacionalidad ignorando mayúsculas, acentos y espacios extra,
// para que un typo de captura (mayúsculas, sin acento, espacio de más) no
// se trate como si fuera un país/nacionalidad distinto.
export function esMexico(valor) {
  return normalizar(valor) === 'MEXICO';
}

export function esMexicana(valor) {
  return normalizar(valor) === 'MEXICANA' || normalizar(valor) === 'MEXICANO';
}

function quitarPrefijos(texto, lista) {
  let palabras = texto.split(' ');
  while (palabras.length > 1 && lista.includes(palabras[0])) {
    palabras = palabras.slice(1);
  }
  return palabras.join(' ');
}

function primeraVocalInterna(palabra) {
  for (let i = 1; i < palabra.length; i++) {
    if (VOCALES.includes(palabra[i])) return palabra[i];
  }
  return 'X';
}

function primeraConsonanteInterna(palabra) {
  const ENYE = 'Ñ';
  for (let i = 1; i < palabra.length; i++) {
    if (!VOCALES.includes(palabra[i])) {
      return palabra[i] === ENYE ? 'X' : palabra[i];
    }
  }
  return 'X';
}

// fechaNacimiento en formato 'YYYY-MM-DD' (input type="date")
function parsearFecha(fechaNacimiento) {
  const [anio, mes, dia] = fechaNacimiento.split('-').map(Number);
  return { anio, mes, dia };
}

function fechaAAMMDD(fechaNacimiento) {
  const { anio, mes, dia } = parsearFecha(fechaNacimiento);
  return String(anio).slice(-2).padStart(2, '0') + String(mes).padStart(2, '0') + String(dia).padStart(2, '0');
}

// ---------------- CURP ----------------

export function calcularCURP({ nombres, apellido_paterno, apellido_materno, fecha_nacimiento, sexo_genero_id, entidad_nacimiento, pais_nacimiento }) {
  if (!nombres || !apellido_paterno || !fecha_nacimiento || !sexo_genero_id) return '';

  const ENYE = 'Ñ';
  const nom = quitarPrefijos(normalizar(nombres, true), SUFIJOS_NOMBRES);
  const pat = normalizar(apellido_paterno, true);
  const mat = normalizar(apellido_materno, true) || 'X';

  const primerNombre = nom.split(' ')[0] || 'X';
  const primerPaterno = pat.split(' ')[0] || 'X';
  const primerMaterno = mat.split(' ')[0] || 'X';

  const { anio } = parsearFecha(fecha_nacimiento);
  const sexoChar = String(sexo_genero_id) === '1' ? 'H' : 'M';
  const entidadCodigo = (pais_nacimiento && !esMexico(pais_nacimiento))
    ? 'NE'
    : (ESTADOS_CURP[entidad_nacimiento] || 'NE');

  let curp = '';
  curp += primerPaterno[0] === ENYE ? 'X' : primerPaterno[0];
  curp += primeraVocalInterna(primerPaterno);
  curp += primerMaterno[0] === ENYE ? 'X' : primerMaterno[0];
  curp += primerNombre[0] === ENYE ? 'X' : primerNombre[0];
  curp += fechaAAMMDD(fecha_nacimiento);
  curp += sexoChar;
  curp += entidadCodigo;
  curp += primeraConsonanteInterna(primerPaterno);
  curp += primeraConsonanteInterna(primerMaterno);
  curp += primeraConsonanteInterna(primerNombre);

  if (PALABRAS_OBSCENAS.includes(curp.substring(0, 4))) {
    curp = curp.substring(0, 1) + 'X' + curp.substring(2);
  }

  // Posicion 17: diferenciador de homonimia (valor inicial sin colision)
  curp += anio >= 2000 ? 'A' : '0';

  // Posicion 18: digito verificador
  const tabla = '0123456789ABCDEFGHIJKLMN' + ENYE + 'OPQRSTUVWXYZ';
  let suma = 0;
  for (let i = 0; i < 17; i++) {
    suma += tabla.indexOf(curp[i]) * (18 - i);
  }
  let digito = 10 - (suma % 10);
  if (digito === 10) digito = 0;
  curp += String(digito);

  return curp;
}

// ---------------- RFC ----------------

const EQUIVALENCIAS_NOMBRE_NUMERICO = {
  'A': '11', 'B': '12', 'C': '13', 'D': '14', 'E': '15', 'F': '16', 'G': '17',
  'H': '18', 'I': '19', 'J': '21', 'K': '22', 'L': '23', 'M': '24', 'N': '25',
  'O': '26', 'P': '27', 'Q': '28', 'R': '29', 'S': '32', 'T': '33', 'U': '34',
  'V': '35', 'W': '36', 'X': '37', 'Y': '38', 'Z': '39', '&': '10'
};

const EQUIVALENCIAS_COCIENTE_RESIDUO = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F',
  'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V',
  'W', 'X', 'Y', 'Z'
];

const VALORES_DIGITO_VERIFICADOR_RFC = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 10, 'B': 11, 'C': 12, 'D': 13, 'E': 14, 'F': 15, 'G': 16, 'H': 17, 'I': 18,
  'J': 19, 'K': 20, 'L': 21, 'M': 22, 'N': 23, 'O': 25, 'P': 26, 'Q': 27, 'R': 28,
  'S': 29, 'T': 30, 'U': 31, 'V': 32, 'W': 33, 'X': 34, 'Y': 35, 'Z': 36, ' ': 37
};

function getPrimerLetra(v) { return v && v.length > 0 ? v[0] : 'X'; }
function getSegundaLetra(v) { return v && v.length > 1 ? v[1] : 'X'; }

function generarLetrasRFC(nombre, apellidoPaterno, apellidoMaterno) {
  let letras = '';
  if (!apellidoMaterno) {
    letras = getPrimerLetra(apellidoPaterno) + getSegundaLetra(apellidoPaterno) + getPrimerLetra(nombre) + getSegundaLetra(nombre);
  } else if (!apellidoPaterno) {
    letras = getPrimerLetra(apellidoMaterno) + getSegundaLetra(apellidoMaterno) + getPrimerLetra(nombre) + getSegundaLetra(nombre);
  } else if (apellidoPaterno.length <= 2) {
    letras = getPrimerLetra(apellidoPaterno) + getPrimerLetra(apellidoMaterno) + getPrimerLetra(nombre) + getSegundaLetra(nombre);
  } else {
    letras = getPrimerLetra(apellidoPaterno) + primeraVocalInterna(apellidoPaterno) + getPrimerLetra(apellidoMaterno) + getPrimerLetra(nombre);
  }
  if (PALABRAS_OBSCENAS.includes(letras)) {
    letras = letras.substring(0, 3) + 'X';
  }
  return letras;
}

function getEquivalenciaNumerica(c) {
  if (EQUIVALENCIAS_NOMBRE_NUMERICO[c]) return EQUIVALENCIAS_NOMBRE_NUMERICO[c];
  if (c >= '0' && c <= '9') return c.padStart(2, '0');
  if (c === ' ') return '00';
  return null;
}

function calcularHomoclaveRFC(nombres, apellidoPaterno, apellidoMaterno) {
  const nombreCompleto = `${apellidoPaterno || ''} ${apellidoMaterno || ''} ${nombres}`.trim();
  let nombreNumerico = '0';
  for (const c of nombreCompleto) {
    const eq = getEquivalenciaNumerica(c);
    if (eq !== null) nombreNumerico += eq;
  }
  let suma = 0;
  for (let i = 0; i < nombreNumerico.length - 1; i++) {
    const d1 = parseInt(nombreNumerico[i], 10);
    const d2 = parseInt(nombreNumerico[i + 1], 10);
    suma += ((d1 * 10) + d2) * d2;
  }
  const base = suma % 1000;
  const cociente = Math.floor(base / 34);
  const residuo = base % 34;
  return EQUIVALENCIAS_COCIENTE_RESIDUO[cociente] + EQUIVALENCIAS_COCIENTE_RESIDUO[residuo];
}

function calcularDigitoVerificadorRFC(rfc12) {
  let suma = 0;
  for (let i = 0; i < 12; i++) {
    const c = i < rfc12.length ? rfc12[i] : ' ';
    suma += (VALORES_DIGITO_VERIFICADOR_RFC[c] || 0) * (13 - i);
  }
  const residuo = suma % 11;
  if (residuo === 0) return '0';
  if (residuo === 1) return 'A';
  return String(11 - residuo);
}

export function calcularRFC({ nombres, apellido_paterno, apellido_materno, fecha_nacimiento }) {
  if (!nombres || (!apellido_paterno && !apellido_materno) || !fecha_nacimiento) return '';

  const nombreLimpio = normalizar(nombres);
  const paternoLimpio = normalizar(apellido_paterno);
  const maternoLimpio = normalizar(apellido_materno);

  const nombreSinSufijos = quitarPrefijos(nombreLimpio, SUFIJOS_NOMBRES);
  const paternoSinSufijos = quitarPrefijos(paternoLimpio, SUFIJOS_APELLIDOS);
  const maternoSinSufijos = quitarPrefijos(maternoLimpio, SUFIJOS_APELLIDOS);

  const letras = generarLetrasRFC(nombreSinSufijos, paternoSinSufijos, maternoSinSufijos);
  const fecha = fechaAAMMDD(fecha_nacimiento);
  const homoclave = calcularHomoclaveRFC(nombreLimpio, paternoLimpio, maternoLimpio);

  const rfcSinDigito = letras + fecha + homoclave;
  const digito = calcularDigitoVerificadorRFC(rfcSinDigito);

  return rfcSinDigito + digito;
}
